import { getErrorMessage } from "@/utils/errorHelpers";
import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FREE_BACKEND_CONFIG } from "@/config/runtime";
import { kycAPI } from "@/services/api";
import { COUNTRIES, getCountryByCode, prefixPhoneWithDialCode } from "@/utils/countries";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, AlertTriangle, CheckCircle, User, Baby, Wallet, FileText, X } from "lucide-react";
import { LoadingState, PageHeader, PageShell } from "@/components/app/page";

const MAX_KYC_PHOTO_SIZE = 8 * 1024 * 1024;

const AGREEMENT_TEXT = `Lorsque je suis agréé comme partenaire de l'entreprise ARTICO-Sarlu, je comprends et accepte que :

Art 1. Le partenaire de ARTICO-Sarlu est une personne physique majeure, à défaut de ce fait, il doit être sous la responsabilité de son tuteur pendant sa prestation dans la Société ;

Art 2. Lors de la signature de cet agrément, et après approbation par le Staff ARTICO-Sarlu, le nouveau partenaire a droit d'être accompagné à chaque étape du processus d'édition jusqu'à la promotion de ses œuvres (produits inédits) sous le label ARTICO, d'acheter les produits à la valeur indiquée dans la plateforme MMJ et de bénéficier de tous les bonus liés à son activité MLM;

Art 3. Le présent agrément constitue un contrat entre le partenaire (ou le tuteur, le cas échéant) et la Société ARTICO-Sarlu à la date où il est réceptionné et validé par le Staff de la société. Le présent agrément autorise le partenaire à exercer son activité dans la plateforme numérique MMJ;

Art 4. Le présent contrat est régi par la législation congolaise conformément à l'Ordonnance-loi n°23/010 portant code du Numérique du 13 Mars 2023 et à l'Arrêté ministériel fixant les conditions et modalités d'examen des demandes et de délivrances des autorisations pour les activités et services numériques. Et le partenaire s'engage à se conformer à toutes les lois fiscales en vigueur en République Démocratique du Congo ;

Art 5. Le partenaire admet que le présent contrat est personnel et ne peut être cédé ou transféré à quiconque sans l'autorisation préalable de la société. Toutefois, en cas d'empêchement justifié, je désigne la personne indiquée ci-dessous pour bénéficier de tous mes droits relatifs à ce partenariat ;

Art 6. Tout partenaire indépendant organise librement son activité sans aucun lien de subordination à l'égard de la société qui n'est pas son employeur et ne saurait par ce fait en assurer les obligations ; le partenaire n'est aucunement un représentant légal de la société. Le partenaire reconnait que sa rémunération est basée sur les ventes de ses produits et sur les performances liées à son activité MLM ;

Art 7. Aussi longtemps que le partenaire se conformera aux dispositions du présent contrat, la société l'assurera pour ses efforts marketing un paiement en conformité avec les différents bonus établis dans le plan marketing de la société ;

Art 8. Le partenaire reconnait avoir lu le « manuel des procédures d'affaires » de la société et accepte librement de mener son affaire en bon et due forme avec ses règlements, procédures et plan marketing. De ce fait, il est responsable de tout acte pouvant entraîner la résiliation du présent contrat d'affaire, entre autres : Descendants fictifs, engager Artico sans autorisation, mineur sans tuteur ;

Art 9. Le partenaire reconnait que la société peut, au moyen d'un préavis raisonnable changer, amender, ou modifier le présent contrat d'affaire et notamment ses procédures, règlements et plan marketing et ce, dans le but d'assurer une parfaite commercialisation de produits ou de s'adapter aux lois et changement de conjoncture économique ;

Art 10. Le partenaire est tenu de visualiser personnellement le produit qu'il a lui-même acheté. Le partenaire a l'accès au produit exclusivement dans la plateforme MMJ ;

Art 11. Le partenaire peut résilier son contrat de plein droit à tout moment par une lettre recommandée avec avis de réception adressée à la société ;

Art 12. Tout différend non réglé à l'amiable entre le partenaire et la société sera soumis à l'arbitrage des instances judiciaires de la RDC. Ainsi, la République Démocratique du Congo sera jugée la seule juridiction compétente pour tous conflits.`;

export default function VerificationPage({ user }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [emailForm, setEmailForm] = useState({ email: user?.email || "", code: "" });
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [phoneConfirmOpen, setPhoneConfirmOpen] = useState(false);

  const [form, setForm] = useState({
    nom: "", postnom: "", prenom: "", sexe: "M",
    date_naissance: "", etat_civil: "", telephone: "",
    adresse_rue: "", adresse_numero: "", adresse_commune: "",
    adresse_ville: "", adresse_province: "", adresse_pays: getCountryByCode("CD").name, country_code: "CD",
    numero_identite: "", type_identite: "carte_identite",
    is_minor: false,
    tuteur_nom: "", tuteur_postnom: "", tuteur_prenom: "",
    tuteur_sexe: "M", tuteur_date_naissance: "", tuteur_etat_civil: "",
    tuteur_telephone: "", tuteur_country_code: "CD", tuteur_email: "", tuteur_adresse: "",
    mobile_money_intitule: "", mobile_money_numero: "",
    banque_nom: "", banque_succursale: "", banque_intitule: "", banque_numero: "",
    bénéficiaire_designation: "", parrain_id: user?.parrain_id || "",
    agreement_accepted: false,
  });
  const [photoId, setPhotoId] = useState(null);
  const [photoProfile, setPhotoProfile] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await kycAPI.getStatus();
      setKycStatus(res.data);
      setEmailForm((prev) => ({ ...prev, email: res.data?.email || user?.email || prev.email }));
    } catch (e) {} finally { setStatusLoading(false); }
  }, [user?.email]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (!kycStatus || kycStatus.status !== "not_submitted") return;
    setForm((prev) => ({
      ...prev,
      telephone: prev.telephone || kycStatus.telephone || "",
      parrain_id: kycStatus.parrain_id || prev.parrain_id || "",
    }));
  }, [kycStatus]);

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const selectCountry = (code) => {
    const country = getCountryByCode(code);
    setForm((prev) => ({
      ...prev,
      country_code: country.code,
      adresse_pays: country.name,
      telephone: prefixPhoneWithDialCode(prev.telephone, country.dialCode),
    }));
  };

  const selectTutorCountry = (code) => {
    const country = getCountryByCode(code);
    setForm((prev) => ({
      ...prev,
      tuteur_country_code: country.code,
      tuteur_telephone: prefixPhoneWithDialCode(prev.tuteur_telephone, country.dialCode),
    }));
  };

  const handleSubmit = async (confirmedPhoneChange = false) => {
    if (!form.agreement_accepted) { toast.error("Vous devez accepter l'agrément"); return; }
    if (!form.nom || !form.prenom || !form.date_naissance || !form.telephone || !form.numero_identite) {
      toast.error("veuillez remplir tous les champs obligatoires"); return;
    }
    if (form.is_minor && (!form.tuteur_nom || !form.tuteur_prenom || !form.tuteur_email || !form.tuteur_telephone)) {
      toast.error("Les informations du tuteur sont obligatoires pour un mineur"); return;
    }
    if (!isValidKycPhoto(photoId, "Photo carte d'identité") || !isValidKycPhoto(photoProfile, "Photo de profil")) {
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      fd.set("phone_change_confirmed", String(confirmedPhoneChange));
      if (photoId) fd.append("photo_identite", photoId);
      if (photoProfile) fd.append("photo_profil", photoProfile);

      await kycAPI.submit(fd);
      toast.success("Dossier KYC soumis avec succès ! En attente de validation.");
      navigate("/dashboard");
    } catch (error) {
      if (error?.response?.status === 409) {
        setPhoneConfirmOpen(true);
        return;
      }
      toast.error(getErrorMessage(error, "Erreur lors de la soumission"));
    } finally { setLoading(false); }
  };

  const requestEmailCode = async () => {
    if (!emailForm.email) {
      toast.error("veuillez saisir votre adresse e-mail");
      return;
    }
    setEmailBusy(true);
    try {
      const res = await kycAPI.requestEmailVerification({ email: emailForm.email });
      setEmailCodeSent(true);
      toast.success(res.data?.message || "Code envoye par e-mail");
    } catch (error) {
      toast.error(getErrorMessage(error, "Envoi du code impossible"));
    } finally {
      setEmailBusy(false);
    }
  };

  const confirmEmailCode = async () => {
    if (!/^\d{6}$/.test(emailForm.code || "")) {
      toast.error("Le code doit contenir 6 chiffres");
      return;
    }
    setEmailBusy(true);
    try {
      const res = await kycAPI.confirmEmailVerification({ email: emailForm.email, code: emailForm.code });
      toast.success(res.data?.message || "Votre e-mail a été vérifié avec succès");
      setEmailCodeSent(false);
      await loadStatus();
    } catch (error) {
      toast.error(getErrorMessage(error, "Verification impossible"));
    } finally {
      setEmailBusy(false);
    }
  };

  if (statusLoading) return <LoadingState />;

  if (kycStatus?.status === "approved" || kycStatus?.is_verified) {
    return (
      <PageShell data-testid="verification-page" className="mx-auto max-w-2xl space-y-4">
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-8 text-center">
            <CheckCircle size={48} className="mx-auto mb-4 text-green-600" />
            <h2 className="text-2xl font-bold text-green-800 font-['Outfit']">Identité vérifiée</h2>
            <p className="text-green-700 mt-2">Votre dossier KYC a été approuvé. Vous avez accès à tous les privilèges partenaire.</p>
          </CardContent>
        </Card>
        <Card className={kycStatus?.email_verified ? "border-green-200 bg-green-50" : "border-[#E6E4E0] bg-white"}>
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">Verification de l'e-mail</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {kycStatus?.email_verified ? (
              <div className="rounded-lg border border-green-200 bg-white p-4 text-green-800">
                Votre e-mail a été vérifié avec succès.
              </div>
            ) : (
              <>
                <p className="text-sm text-[#5C625F]">
                  Saisissez l'e-mail de votre compte. Un code a six chiffres vous sera envoye par Artico-MMJ. Le code expire après 20 minutes et 6 essais maximum sont autorises.
                </p>
                <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                  <div>
                    <Label>E-mail du compte</Label>
                    <Input
                      type="email"
                      value={emailForm.email}
                      onChange={(e) => setEmailForm((prev) => ({ ...prev, email: e.target.value }))}
                      className="mt-1"
                      data-testid="kyc-email-verification-email"
                    />
                  </div>
                  <Button type="button" onClick={requestEmailCode} disabled={emailBusy} className="self-end bg-[#2A4B46] text-white">
                    {emailBusy ? "Envoi..." : "Recevoir le code"}
                  </Button>
                </div>
                {emailCodeSent && (
                  <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                    <div>
                      <Label>Code a 6 chiffres</Label>
                      <Input
                        inputMode="numeric"
                        maxLength={6}
                        value={emailForm.code}
                        onChange={(e) => setEmailForm((prev) => ({ ...prev, code: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                        className="mt-1 tracking-[0.35em]"
                        data-testid="kyc-email-verification-code"
                      />
                    </div>
                    <Button type="button" onClick={confirmEmailCode} disabled={emailBusy || emailForm.code.length !== 6} className="self-end bg-green-700 text-white">
                      {emailBusy ? "Verification..." : "Verifier"}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  if (kycStatus?.status === "pending") {
    return (
      <PageShell data-testid="verification-page" className="mx-auto max-w-2xl">
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="py-8 text-center">
            <AlertTriangle size={48} className="mx-auto mb-4 text-yellow-600" />
            <h2 className="text-2xl font-bold text-yellow-800 font-['Outfit']">Dossier en cours de vérification</h2>
            <p className="text-yellow-700 mt-2">Votre dossier KYC est en attente de validation par un administrateur.</p>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  const totalSteps = form.is_minor ? 5 : 4;

  return (
    <PageShell data-testid="verification-page" className="mx-auto max-w-3xl">
      <PageHeader
        title="Vérification d'identité"
        description="Contrat d'affaire avec ARTICO - Remplissez tous les champs pour valider votre partenariat"
      />

      {/* Progress */}
      <div className="flex items-center gap-2">
        {Array.from({ length: totalSteps }, (_, i) => (
          <div key={i} className={`flex-1 h-2 rounded-full ${i < step ? "bg-[#2A4B46]" : "bg-[#E6E4E0]"}`} />
        ))}
      </div>

      {/* Step 1: Identity */}
      {step === 1 && (
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit'] flex items-center gap-2">
              <User size={20} /> Identité détaillée du partenaire
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div><Label>Nom *</Label><Input data-testid="kyc-nom" value={form.nom} onChange={(e) => set("nom", e.target.value)} required className="mt-1" /></div>
              <div><Label>Post-nom</Label><Input data-testid="kyc-postnom" value={form.postnom} onChange={(e) => set("postnom", e.target.value)} className="mt-1" /></div>
              <div><Label>Prénom *</Label><Input data-testid="kyc-prenom" value={form.prenom} onChange={(e) => set("prenom", e.target.value)} required className="mt-1" /></div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div><Label>Sexe</Label>
                <Select value={form.sexe} onValueChange={(v) => set("sexe", v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="M">Masculin</SelectItem><SelectItem value="F">Féminin</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Date de naissance *</Label><Input type="date" data-testid="kyc-dob" value={form.date_naissance} onChange={(e) => set("date_naissance", e.target.value)} required className="mt-1" /></div>
              <div><Label>État civil</Label>
                <Select value={form.etat_civil} onValueChange={(v) => set("etat_civil", v)}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="celibataire">Célibataire</SelectItem>
                    <SelectItem value="marie">Marié(e)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Pays *</Label>
              <CountrySelect value={form.country_code} onValueChange={selectCountry} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Téléphone *</Label><Input data-testid="kyc-phone" value={form.telephone} onChange={(e) => set("telephone", e.target.value)} required className="mt-1" placeholder="+243..." /></div>
              <div><Label>N° Identité / OP Passeport *</Label><Input data-testid="kyc-id-number" value={form.numero_identite} onChange={(e) => set("numero_identite", e.target.value)} required className="mt-1" /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Photo carte d'identité</Label><Input type="file" accept="image/*" onChange={(e) => setPhotoId(e.target.files[0])} className="mt-1" data-testid="kyc-photo-id" /></div>
              <div><Label>Photo de profil</Label><Input type="file" accept="image/*" onChange={(e) => setPhotoProfile(e.target.files[0])} className="mt-1" data-testid="kyc-photo-profile" /></div>
            </div>
            <div><Label>Adresse (Av./Rue, N°, Commune, Ville, Province, Pays)</Label>
              <div className="grid md:grid-cols-3 gap-2 mt-1">
                <Input placeholder="Av./Rue" value={form.adresse_rue} onChange={(e) => set("adresse_rue", e.target.value)} />
                <Input placeholder="N°" value={form.adresse_numero} onChange={(e) => set("adresse_numero", e.target.value)} />
                <Input placeholder="Commune" value={form.adresse_commune} onChange={(e) => set("adresse_commune", e.target.value)} />
                <Input placeholder="Ville" value={form.adresse_ville} onChange={(e) => set("adresse_ville", e.target.value)} />
                <Input placeholder="Province" value={form.adresse_province} onChange={(e) => set("adresse_province", e.target.value)} />
                <Input placeholder="Pays" value={form.adresse_pays} readOnly />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Checkbox checked={form.is_minor} onCheckedChange={(v) => set("is_minor", !!v)} data-testid="kyc-is-minor" />
              <Label className="cursor-pointer">Je suis mineur(e) (moins de 18 ans)</Label>
            </div>
            <div>
              <Label>Code parrainage (facultatif)</Label>
              <Input
                value={form.parrain_id}
                onChange={(e) => set("parrain_id", e.target.value.toUpperCase())}
                className="mt-1"
                placeholder="Ex: ART243002"
                readOnly={!!kycStatus?.parrain_id}
              />
              {kycStatus?.parrain_id ? (
                <p className="mt-1 text-xs text-[#5C625F]">Ce code vient de votre inscription. Votre lignee de parrainage ne peut pas etre modifiee.</p>
              ) : (
                <p className="mt-1 text-xs text-[#5C625F]">Si vous n'avez pas indique de parrain a l'inscription, ce code sera rattache a votre compte après validation KYC.</p>
              )}
            </div>
            <Button className="w-full bg-[#2A4B46] text-white" onClick={() => setStep(form.is_minor ? 2 : 3)} data-testid="kyc-next-1">Suivant</Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Tutor (minor only) */}
      {step === 2 && form.is_minor && (
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit'] flex items-center gap-2">
              <Baby size={20} /> Identité détaillée du tuteur
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
              En tant que mineur, vous devez fournir les informations de votre tuteur. Un compte partenaire sera automatiquement créé pour votre tuteur.
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div><Label>Nom du tuteur *</Label><Input data-testid="kyc-tuteur-nom" value={form.tuteur_nom} onChange={(e) => set("tuteur_nom", e.target.value)} required className="mt-1" /></div>
              <div><Label>Post-nom</Label><Input value={form.tuteur_postnom} onChange={(e) => set("tuteur_postnom", e.target.value)} className="mt-1" /></div>
              <div><Label>Prénom du tuteur *</Label><Input data-testid="kyc-tuteur-prenom" value={form.tuteur_prenom} onChange={(e) => set("tuteur_prenom", e.target.value)} required className="mt-1" /></div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div><Label>Sexe</Label>
                <Select value={form.tuteur_sexe} onValueChange={(v) => set("tuteur_sexe", v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="M">Masculin</SelectItem><SelectItem value="F">Féminin</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Date de naissance</Label><Input type="date" value={form.tuteur_date_naissance} onChange={(e) => set("tuteur_date_naissance", e.target.value)} className="mt-1" /></div>
              <div><Label>État civil</Label>
                <Select value={form.tuteur_etat_civil} onValueChange={(v) => set("tuteur_etat_civil", v)}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="celibataire">Célibataire</SelectItem>
                    <SelectItem value="marie">Marié(e)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Pays tuteur</Label>
              <CountrySelect value={form.tuteur_country_code} onValueChange={selectTutorCountry} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Téléphone tuteur *</Label><Input data-testid="kyc-tuteur-phone" value={form.tuteur_telephone} onChange={(e) => set("tuteur_telephone", e.target.value)} required className="mt-1" /></div>
              <div><Label>Email tuteur *</Label><Input type="email" data-testid="kyc-tuteur-email" value={form.tuteur_email} onChange={(e) => set("tuteur_email", e.target.value)} required className="mt-1" /></div>
            </div>
            <div><Label>Adresse tuteur</Label><Input value={form.tuteur_adresse} onChange={(e) => set("tuteur_adresse", e.target.value)} className="mt-1" /></div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setStep(1)}>Retour</Button>
              <Button className="flex-1 bg-[#2A4B46] text-white" onClick={() => setStep(3)} data-testid="kyc-next-2">Suivant</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Financial info */}
      {step === 3 && (
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit'] flex items-center gap-2">
              <Wallet size={20} /> Informations financières
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold text-[#2A4B46] mb-3">Mobile Money</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Intitulé du compte</Label><Input value={form.mobile_money_intitule} onChange={(e) => set("mobile_money_intitule", e.target.value)} className="mt-1" /></div>
                <div><Label>Numéro du compte</Label><Input value={form.mobile_money_numero} onChange={(e) => set("mobile_money_numero", e.target.value)} className="mt-1" placeholder="+243..." /></div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-[#2A4B46] mb-3">Détails bancaires (optionnel)</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Nom de la banque</Label><Input value={form.banque_nom} onChange={(e) => set("banque_nom", e.target.value)} className="mt-1" /></div>
                <div><Label>Succursale</Label><Input value={form.banque_succursale} onChange={(e) => set("banque_succursale", e.target.value)} className="mt-1" /></div>
                <div><Label>Intitulé du compte</Label><Input value={form.banque_intitule} onChange={(e) => set("banque_intitule", e.target.value)} className="mt-1" /></div>
                <div><Label>Numéro de compte</Label><Input value={form.banque_numero} onChange={(e) => set("banque_numero", e.target.value)} className="mt-1" /></div>
              </div>
            </div>
            <div><Label>Bénéficiaire désigné (Art. 5 — en cas d'empêchement)</Label><Input value={form.bénéficiaire_designation} onChange={(e) => set("bénéficiaire_designation", e.target.value)} className="mt-1" placeholder="Nom complet du bénéficiaire" /></div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setStep(form.is_minor ? 2 : 1)}>Retour</Button>
              <Button className="flex-1 bg-[#2A4B46] text-white" onClick={() => setStep(form.is_minor ? 4 : 4)} data-testid="kyc-next-3">Suivant</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Agreement */}
      {step === (form.is_minor ? 4 : 4) && (
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit'] flex items-center gap-2">
              <FileText size={20} /> Agrément — Contrat d'affaire avec ARTICO
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="max-h-96 overflow-y-auto p-4 bg-[#F3EFE9] rounded-lg border border-[#E6E4E0] text-sm text-[#5C625F] whitespace-pre-wrap leading-relaxed" data-testid="agreement-text">
              {AGREEMENT_TEXT}
            </div>
            {/* Note d'information sur l'activation (Règle Alain) */}
<div className="my-6 p-4 bg-amber-50 border border-amber-200 rounded-lg shadow-sm">
  <p className="text-[#8B6F47] text-sm leading-relaxed">
    <strong className="block text-[#2A4B46] mb-1">⚠️ Note importante sur votre activation :</strong>
    Conformément aux procédures MMJ, la validation ultérieure 
    par l'administration vaut <strong> Point-Activation (1 MC)</strong>.
    C'est cette validation qui débloquera 
    votre statut de partenaire <strong>P-00</strong> et vos futurs bonus.
  </p>
</div>
            <div className="flex items-start gap-3 p-4 bg-white border border-[#E6E4E0] rounded-lg">
              <Checkbox
                checked={form.agreement_accepted}
                onCheckedChange={(v) => set("agreement_accepted", !!v)}
                data-testid="kyc-agreement-checkbox"
                className="mt-1"
              />
              <Label className="cursor-pointer text-sm leading-relaxed">
                J'ai lu et j'approuve l'intégralité du contrat d'affaire avec ARTICO-Sarlu. Je comprends et accepte les 12 articles ci-dessus.
              </Label>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setStep(3)}>Retour</Button>
              <Button
                className="flex-1 bg-[#2A4B46] text-white"
                onClick={() => handleSubmit(false)}
                disabled={loading || !form.agreement_accepted}
                data-testid="kyc-submit-button"
              >
                {loading ? "Soumission..." : "Soumettre mon dossier"}
              </Button>
            </div>
            {loading && (
              <p className="text-center text-xs text-[#5C625F]">
                {FREE_BACKEND_CONFIG.uploadWaitMessage}
              </p>
            )}
          </CardContent>
        </Card>
      )}
      <Dialog open={phoneConfirmOpen} onOpenChange={setPhoneConfirmOpen}>
        <DialogContent className="max-w-md border-[#E6E4E0]">
          <button
            type="button"
            onClick={() => setPhoneConfirmOpen(false)}
            className="absolute right-4 top-4 rounded-full bg-red-50 p-1 text-red-700 hover:bg-red-100"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
          <DialogHeader>
            <DialogTitle className="pr-8 text-[#2A4B46]">Confirmer le changement de telephone</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-[#5C625F]">
            <p>
              Le numéro saisi dans le KYC est different de celui utilise a l'inscription.
              Si vous confirmez, le nouveau numéro remplacera l'ancien seulement après validation par un admin.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={() => setPhoneConfirmOpen(false)}>
                Non, je modifie
              </Button>
              <Button type="button" className="bg-[#2A4B46] text-white hover:bg-[#1f3a36]" onClick={() => {
                setPhoneConfirmOpen(false);
                handleSubmit(true);
              }}>
                Oui, confirmer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}

function CountrySelect({ value, onValueChange }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-72">
        {COUNTRIES.map((country) => (
          <SelectItem key={country.code} value={country.code}>{country.name} ({country.dialCode})</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function isValidKycPhoto(file, label) {
  if (!file) return true;
  if (file.size > MAX_KYC_PHOTO_SIZE) {
    toast.error(`${label}: fichier trop lourd. Maximum autorisé: 8 MB.`);
    return false;
  }
  return true;
}
